import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Copy, Shield, User as UserIcon, KeyRound } from 'lucide-react';
import {
  appAdminListUsers, appAdminListPages, appAdminCreateInvite,
  appAdminUpdateUser, appAdminRevokeUser, appAdminRevokeInvite,
  appAdminResetUserPassword,
  formatApiErrorDetail,
} from '@/lib/appApi';
import { useAppAuth } from '@/contexts/AppAuthContext';

const PAGE_LABELS = {
  'dashboard': 'Dashboard', 'calendar': 'Calendar', 'operations': 'Operations',
  'finance': 'Finance', 'financial-health': 'Financial Health',
  'vacancy': 'Vacancy', 'budgeting': 'Budgeting',
  'tenants': 'Tenants', 'notes': 'Notes',
  'move-in-out': 'Move In/Out', 'housekeeping': 'Housekeeping',
  'messages': 'Messages', 'leads': 'Leads',
  'properties': 'Properties', 'listings': 'Listings',
  'furniture-projects': 'Furniture Projects', 'staff-management': 'Staff Management',
  'activity-log': 'Activity Log', 'admin-users': 'Admin · Users',
};

export default function AdminUsersPage() {
  const { user: me } = useAppAuth();
  const [data, setData] = useState({ users: [], pending_invites: [] });
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createdInvite, setCreatedInvite] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([appAdminListUsers(), appAdminListPages()]);
      setData(u); setPages(p.pages);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load users');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  async function handleRevokeUser(u) {
    if (!window.confirm(`Revoke dashboard access for ${u.name}? Their other access (e.g. furniture-staff) will remain.`)) return;
    try { await appAdminRevokeUser(u.id); toast.success('Access revoked'); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }
  async function handleRevokeInvite(inv) {
    if (!window.confirm(`Cancel invite for ${inv.name}?`)) return;
    try { await appAdminRevokeInvite(inv.id); toast.success('Invite cancelled'); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }
  async function handleResetPassword(u) {
    if (!window.confirm(`Reset the password for ${u.display_name || u.name}? Their existing password will stop working immediately and you'll need to share the new one with them.`)) return;
    try {
      const res = await appAdminResetUserPassword(u.id);
      setResetResult(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Could not reset password');
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-0.5">Invite teammates and choose which pages they can access.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="admin-users-invite-btn">
          <Plus className="h-4 w-4" /> Invite user
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Active users</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left py-2 px-4">Name</th>
                <th className="text-left py-2 px-4">Email</th>
                <th className="text-left py-2 px-4">Role</th>
                <th className="text-left py-2 px-4">Pages</th>
                <th className="py-2 px-4 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-6 text-gray-400">Loading...</td></tr>}
              {!loading && data.users.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-gray-400">No users yet — invite the first one above.</td></tr>
              )}
              {data.users.map((u) => (
                <tr key={u.id} className="border-t hover:bg-gray-50/60" data-testid={`admin-user-row-${u.id}`}>
                  <td className="py-2.5 px-4 flex items-center gap-2">
                    {u.app_role === 'admin' ? <Shield className="h-3.5 w-3.5 text-emerald-700" /> : <UserIcon className="h-3.5 w-3.5 text-gray-400" />}
                    {u.display_name || u.name}
                  </td>
                  <td className="py-2.5 px-4 text-gray-600">{u.email}</td>
                  <td className="py-2.5 px-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${u.app_role === 'admin' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                      {u.app_role}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-xs text-gray-500">
                    {u.app_role === 'admin' ? 'All pages' : `${(u.app_pages || []).length} pages`}
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(u)} className="text-xs h-7" data-testid={`admin-user-edit-${u.id}`}>Edit</Button>
                    {u.id !== me?.id && (
                      <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u)} className="text-xs h-7 text-amber-700 hover:text-amber-800" title="Reset password" data-testid={`admin-user-reset-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {u.id !== me?.id && (
                      <Button size="sm" variant="ghost" onClick={() => handleRevokeUser(u)} className="text-xs h-7 text-destructive" data-testid={`admin-user-revoke-${u.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Pending invites</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left py-2 px-4">Name</th>
                <th className="text-left py-2 px-4">Role</th>
                <th className="text-left py-2 px-4">PIN</th>
                <th className="text-left py-2 px-4">Pages</th>
                <th className="py-2 px-4 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && data.pending_invites.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-gray-400">No pending invites.</td></tr>
              )}
              {data.pending_invites.map((inv) => (
                <tr key={inv.id} className="border-t hover:bg-gray-50/60" data-testid={`admin-invite-row-${inv.id}`}>
                  <td className="py-2.5 px-4">{inv.name}</td>
                  <td className="py-2.5 px-4 capitalize">{inv.app_role}</td>
                  <td className="py-2.5 px-4 font-mono text-emerald-700 text-base">{inv.pin}</td>
                  <td className="py-2.5 px-4 text-xs text-gray-500">{inv.app_role === 'admin' ? 'All' : (inv.app_pages || []).length}</td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(inv.pin); toast.success('PIN copied'); }} className="text-xs h-7" data-testid={`admin-invite-copy-${inv.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleRevokeInvite(inv)} className="text-xs h-7 text-destructive" data-testid={`admin-invite-revoke-${inv.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showCreate && (
        <CreateInviteDialog open={showCreate} onClose={() => setShowCreate(false)} pages={pages}
          onCreated={(inv) => { setShowCreate(false); setCreatedInvite(inv); load(); }} />
      )}

      {editing && (
        <EditUserDialog user={editing} pages={pages} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {createdInvite && (
        <Dialog open onOpenChange={() => setCreatedInvite(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite created</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-gray-600">Share this PIN with <strong>{createdInvite.name}</strong>. They go to <code>/register</code> and enter it.</p>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-4">
                <span className="font-mono text-3xl tracking-widest text-emerald-700 flex-1 text-center" data-testid="created-invite-pin">{createdInvite.pin}</span>
                <Button onClick={() => { navigator.clipboard.writeText(createdInvite.pin); toast.success('Copied'); }} variant="outline" size="sm">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-400">PIN is one-time use. Share via secure channel (text, signal, etc).</p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {resetResult && (
        <Dialog open onOpenChange={() => setResetResult(null)}>
          <DialogContent data-testid="reset-password-result">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-700" /> Temporary password generated</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-gray-600">
                New temporary password for <strong>{resetResult.user.display_name || resetResult.user.email}</strong>.
                Their old password no longer works — copy this and share it with them right now.
              </p>
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <span className="font-mono text-xl tracking-wider text-amber-800 flex-1 text-center break-all" data-testid="reset-password-temp">{resetResult.temp_password}</span>
                <Button onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success('Copied'); }} variant="outline" size="sm" data-testid="reset-password-copy">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                This password is shown <strong>once</strong>. After closing this dialog there is no way to retrieve it again — generate another reset if you lose it.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ── Create-invite dialog ─────────────────────────────────── */
function CreateInviteDialog({ open, onClose, pages, onCreated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [appRole, setAppRole] = useState('user');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const togglePage = (slug) => setSelected((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const inv = await appAdminCreateInvite({
        name: name.trim(),
        email: email.trim() || null,
        app_role: appRole,
        app_pages: appRole === 'admin' ? [] : selected,
      });
      toast.success('Invite created');
      onCreated(inv);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Could not create invite');
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Invite a user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-gray-500">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name" /></div>
            <div><Label className="text-xs text-gray-500">Email <span className="text-gray-400">(optional)</span></Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" /></div>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Role</Label>
            <Select value={appRole} onValueChange={setAppRole}>
              <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User — restricted to selected pages</SelectItem>
                <SelectItem value="admin">Admin — full access + can manage users</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {appRole === 'user' && (
            <div>
              <Label className="text-xs text-gray-500">Pages they can access</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5 max-h-64 overflow-y-auto p-2 border rounded-lg">
                {pages.map((slug) => (
                  <label key={slug} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded" data-testid={`invite-page-${slug}`}>
                    <Checkbox checked={selected.includes(slug)} onCheckedChange={() => togglePage(slug)} />
                    <span>{PAGE_LABELS[slug] || slug}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">{selected.length} page(s) selected</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !name.trim() || (appRole === 'user' && selected.length === 0)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="invite-submit">
              {busy ? 'Creating...' : 'Generate PIN'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Edit-user dialog ─────────────────────────────────── */
function EditUserDialog({ user, pages, onClose, onSaved }) {
  const { user: me } = useAppAuth();
  const [appRole, setAppRole] = useState(user.app_role || 'user');
  const [selected, setSelected] = useState(user.app_pages || []);
  const [displayName, setDisplayName] = useState(user.display_name || user.name || '');
  const [busy, setBusy] = useState(false);
  const isSelf = me?.id === user.id;

  const togglePage = (slug) => setSelected((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);

  async function save() {
    setBusy(true);
    try {
      await appAdminUpdateUser(user.id, {
        display_name: displayName,
        app_role: appRole,
        app_pages: appRole === 'admin' ? null : selected,
      });
      toast.success('User updated');
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Edit {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-gray-500">Display name (shown in greeting)</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="edit-user-display-name" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Role</Label>
            <Select value={appRole} onValueChange={setAppRole} disabled={isSelf}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            {isSelf && <p className="text-[11px] text-gray-400 mt-1">You cannot demote yourself.</p>}
          </div>
          {appRole === 'user' && (
            <div>
              <Label className="text-xs text-gray-500">Pages they can access</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5 max-h-64 overflow-y-auto p-2 border rounded-lg">
                {pages.map((slug) => (
                  <label key={slug} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                    <Checkbox checked={selected.includes(slug)} onCheckedChange={() => togglePage(slug)} />
                    <span>{PAGE_LABELS[slug] || slug}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="edit-user-save">
              {busy ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
